import { sha256Hex } from '../reportGeneration/reportContractHash';
import type { FrozenDesignLoadMarginSources } from './sourceStatedDesignLoadMargin';
import { extractNumberedRefIds } from '../citationTokens';

/** A current owner planning input, never an issued tariff or a PPA fact. */
export interface SourceFinancialPlanningEnergyPrice {
  refId: number;
  documentId: string;
  phaseId: string;
  priceUsdPerMwh: number;
  demandChargeUsdPerKwMonth: number;
  billedPeakMw: number;
}

const NUMBER = '(\\d[\\d,]*(?:\\.\\d+)?)';
const amount = (value: string): number => Number(value.replace(/,/g, ''));
const sourcePrice = new RegExp(`^(?:Tariff\\s+inputs\\s+)?Current financial planning energy price is \\$${NUMBER}/kWh \\(\\$${NUMBER}/MWh\\), plus \\$${NUMBER} per billed facility kW per month on ${NUMBER} MW billed peak$`, 'i');

/** Bind selected full source bytes and phase identity before admitting a role.
 * Explicit kWh/MWh values are checked for consistency, never filled by inference. */
export function buildSourceFinancialPlanningEnergyPrices(args: {
  references?: readonly { field?: string; documentId?: string; url?: string }[];
  selectedDocumentIds: readonly string[];
  sources?: FrozenDesignLoadMarginSources;
}): SourceFinancialPlanningEnergyPrice[] {
  if (!args.sources) return [];
  const out: SourceFinancialPlanningEnergyPrice[] = [];
  for (const [index, ref] of (args.references ?? []).entries()) {
    if (ref.field !== 'documents' || !ref.documentId || ref.url !== `doc://${ref.documentId}`
      || !args.selectedDocumentIds.includes(ref.documentId)
      || (args.references ?? []).filter(row => row.field === 'documents' && row.documentId === ref.documentId).length !== 1) continue;
    const docs = args.sources.documents.filter(row => row.id === ref.documentId);
    const receipts = args.sources.manifest.filter(row => row.documentId === ref.documentId);
    if (docs.length !== 1 || receipts.length !== 1) continue;
    const doc = docs[0]!, receipt = receipts[0]!;
    if (!doc.fullExtractedText || doc.contentHash !== receipt.originalFileSha256
      || !/^[a-f\d]{64}$/i.test(receipt.originalFileSha256) || !/^[a-f\d]{64}$/i.test(receipt.bodySha256)
      || sha256Hex(doc.fullExtractedText) !== receipt.bodySha256
      || doc.storagePath !== receipt.storagePath || doc.rawStoragePath !== receipt.rawStoragePath
      || doc.extractedCharsKept !== receipt.extractedCharsKept || doc.fullExtractedText.length !== receipt.extractedCharsKept) continue;
    const rows = doc.fullExtractedText.split(/[.!?](?=\s|$)|[;\n|]/).map(row => row.trim()).filter(Boolean);
    const phaseRows = rows.filter(row => /\bselected\s+phase\b/i.test(row));
    if (phaseRows.length !== 1 || /\b(?:not|no|if|unless|unknown|withdrawn|superseded|rejected)\b/i.test(phaseRows[0]!)) continue;
    const phaseIds = [...phaseRows[0]!.matchAll(/\bselected\s+phase\s+([A-Za-z\d][A-Za-z\d_-]*)\b/gi)];
    if (phaseIds.length !== 1 || args.sources.phases.filter(phase => phase.id === phaseIds[0]![1]).length !== 1) continue;
    const priceRows = rows.filter(row => /\bcurrent\s+financial\s+planning\s+energy\s+price\b/i.test(row));
    if (priceRows.length !== 1) continue;
    const match = sourcePrice.exec(priceRows[0]!);
    const position = rows.indexOf(priceRows[0]!);
    if (!match || !/^These are estimates, not utility-issued tariffs$/i.test(rows[position + 1] ?? '')) continue;
    const [priceUsdPerKwh, priceUsdPerMwh, demandChargeUsdPerKwMonth, billedPeakMw] = match.slice(1).map(amount);
    if (![priceUsdPerKwh, priceUsdPerMwh, demandChargeUsdPerKwMonth, billedPeakMw].every(value => Number.isFinite(value) && value! > 0)
      || Math.abs(priceUsdPerKwh! * 1_000 - priceUsdPerMwh!) > Number.EPSILON * Math.max(1, priceUsdPerMwh!) * 4) continue;
    out.push({ refId: index + 1, documentId: doc.id, phaseId: phaseIds[0]![1]!,
      priceUsdPerMwh: priceUsdPerMwh!, demandChargeUsdPerKwMonth: demandChargeUsdPerKwMonth!, billedPeakMw: billedPeakMw! });
  }
  return out.filter(row => !out.some(other => other.phaseId === row.phaseId
    && (other.priceUsdPerMwh !== row.priceUsdPerMwh || other.demandChargeUsdPerKwMonth !== row.demandChargeUsdPerKwMonth || other.billedPeakMw !== row.billedPeakMw)));
}

/** Invalid attempts fail closed even if their value matches an unrelated LCOE.
 * A verified citation identifies source bytes; the financial input stays estimated. */
export function checkSourceFinancialPlanningEnergyPrice(args: {
  text: string; index: number; raw: string; value: number; unit: string; rangeLow?: number;
  evidence?: readonly SourceFinancialPlanningEnergyPrice[];
}): { supported: boolean; expected: string } | undefined {
  if (args.unit !== 'USD/MWh') return undefined;
  const barrier = /[.!?](?=\s|$)|[;\n|]/gi;
  const prior = [...args.text.slice(0, args.index).matchAll(barrier)].at(-1);
  const start = prior ? prior.index! + prior[0].length : 0;
  const next = new RegExp(barrier.source, 'i').exec(args.text.slice(args.index + args.raw.length));
  const clause = args.text.slice(start, next ? args.index + args.raw.length + next.index : undefined).trim();
  if (!/\bfinancial\s+planning\s+energy\s+price\b/i.test(clause)) return undefined;
  const expected = 'The exact estimated financial planning energy price in a current selected frozen full-document source, with its own numbered document reference; PPA, fuel, LCOE and utility-issued tariff authority remain independent.';
  const pattern = new RegExp(`^(?:-\\s*[A-Za-z][A-Za-z ]{0,63}:\\s*)?The financial planning energy price is estimated at \\$${NUMBER}/MWh(?: plus a \\$${NUMBER} per billed facility kW per month demand charge on the ${NUMBER} MW billed peak)? \\[(?:verified\\s+)?Ref\\s+([1-9]\\d*)\\]$`, 'i');
  const match = pattern.exec(clause);
  const token = /\$\d[\d,]*(?:\.\d+)?\/MWh/i.exec(clause);
  const refIds = extractNumberedRefIds(clause);
  const clauseStart = args.text.indexOf(clause, start);
  const supported = match && token && clauseStart >= 0 && args.index === clauseStart + token.index
    && args.raw === token[0] && args.value === amount(match[1]!) && (args.rangeLow == null || args.rangeLow === args.value)
    && refIds.length === 1 && refIds[0] === Number(match[4])
    && args.evidence?.some(row => row.refId === refIds[0] && row.priceUsdPerMwh === args.value
      && (match[2] == null || (row.demandChargeUsdPerKwMonth === amount(match[2]) && row.billedPeakMw === amount(match[3]!))));
  return { supported: Boolean(supported), expected };
}
